import { getTemplatePaginationStyles } from './Pagination.styles'
import { tryLoadDependencies, WebAwesomeComponent } from '../Global/loadDependency'
import type { CallbackEvent } from './types/Pagination.type'
import type { PaginationProps } from './types/Pagination.type'
import { generatePaginationNumbers } from './utils'
import { dispatchAnalyticEvent } from '../Global/analyticsEvents'

const prevButtonTemplate = document.createElement('template')
prevButtonTemplate.innerHTML = `
<wa-button exportparts="base:pagination-prev" @click="prevPage" appearance="outlined">
  <wa-icon name="chevron-left" label="previous"></wa-icon>
</wa-button>
`

const pageGapTemplate = document.createElement('template')
pageGapTemplate.innerHTML = `
  <wa-button exportparts="base:pagination-gap" disabled>...</wa-button>
`

const nextButtonTemplate = document.createElement('template')
nextButtonTemplate.innerHTML = `
<wa-button exportparts="base:pagination-next" @click="nextPage" appearance="outlined">
  <wa-icon name="chevron-right" label="next"></wa-icon>
</wa-button>
`

const pageEntryTemplate = document.createElement('template')
pageEntryTemplate.innerHTML = `
  <wa-button exportparts="base:pagination-page" variant="neutral" appearance="outlined filled" @click="goToPage(page)">{{ page }}</wa-button>
`

const t = document.createElement('template')
t.innerHTML = `
<div class="ts-split" part="pagination-container">
  <span class="wa-caption-l">Showing page {{ currentPage }} of {{ totalPages }} pages</span>
  <wa-button-group orientation="horizontal" part="pagination-buttons">
  </wa-button-group>
</div>
`
const TemplatePagination = t

class Pagination extends HTMLElement {
  private readonly componentName = 'ts-pagination'
  private pageInfo: PaginationProps | null = null
  private _callback: ((e: CallbackEvent) => void) | null = null
  private static PageChangeEventName = 'ascend_pagination_pagechanged'

  constructor() {
    super()

    const shadow = this.attachShadow({ mode: 'open' })
    shadow.appendChild(getTemplatePaginationStyles().content.cloneNode(true))
    shadow.appendChild(TemplatePagination.content.cloneNode(true))

    tryLoadDependencies(document, [WebAwesomeComponent.Button, WebAwesomeComponent.ButtonGroup])
  }

  set callback(callback: (e: CallbackEvent) => void) {
    this._callback = callback
  }

  connectedCallback() {
    dispatchAnalyticEvent('ascend_pagination_loaded', 'load', this.componentName)
  }

  static get observedAttributes() {
    return ['data']
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (name === 'data') {
      this.pageInfo = this.tryGetPagination()
      this.reload()
    }
  }

  private tryGetPagination(): PaginationProps | null {
    const dataPagination = this.getAttribute('data')
    if (!dataPagination) {
      return null
    }
    try {
      return JSON.parse(dataPagination)
    } catch (e) {
      console.error('Invalid JSON:', e)
    }
    return null
  }

  private fireSelectedPageChange(page: number) {
    if (this.pageInfo) {
      this.pageInfo.currentPage = page
    }
    this.reload()
    this._callback?.({ actionType: 'page-selected', page })
  }

  private reload() {
    if (this.pageInfo == null || (this.pageInfo.autoHide && this.pageInfo.totalPages <= 1)) {
      this.shadowRoot?.querySelector('.ts-split')?.classList.add('hide')
      return
    }

    this.shadowRoot?.querySelector('.ts-split')?.classList.remove('hide')

    const paginationClone = this.shadowRoot
    if (!paginationClone) return

    const { currentPage, totalPages } = this.pageInfo

    const pages = generatePaginationNumbers(currentPage, totalPages)

    const currentPageSpan = paginationClone.querySelector('span')
    if (currentPageSpan) {
      currentPageSpan.textContent = `Page ${this.pageInfo.currentPage} of ${this.pageInfo.totalPages}`
    }

    const pageList = paginationClone.querySelector('wa-button-group')
    if (pageList) {
      pageList.innerHTML = ''

      // prev button
      const prevButton = prevButtonTemplate.content.cloneNode(true) as HTMLElement
      const prevButtonElement = prevButton.querySelector('wa-button')
      if (prevButtonElement) {
        prevButtonElement.addEventListener('click', () => {
          if (this.pageInfo && this.pageInfo.currentPage > 1) {
            // Fire analytics event for table sorting
            dispatchAnalyticEvent(Pagination.PageChangeEventName, 'click', 'ts-pagination', {
              label: 'previous',
              context: this.pageInfo.analyticsContextKey ?? 'self',
            })
            this.fireSelectedPageChange(this.pageInfo.currentPage - 1)
          }
        })
      }
      pageList?.appendChild(prevButton)

      // pages
      for (let i = 0; i < pages.length; i++) {
        const pageEntry = pageEntryTemplate.content.cloneNode(true) as HTMLElement
        const pageButton = pageEntry.querySelector('wa-button')
        if (pageButton) {
          if (pages[i] === -1) {
            // gap
            pageButton.setAttribute('disabled', 'true')
            pageButton.textContent = '...'
            pageList.appendChild(pageEntry)
            continue
          }
          pageButton.textContent = pages[i].toString()

          if (pages[i] === this.pageInfo.currentPage) {
            pageButton.setAttribute('variant', 'brand')
          }

          pageButton.addEventListener('click', () => {
            dispatchAnalyticEvent(Pagination.PageChangeEventName, 'click', 'ts-pagination', {
              label: `${pageButton.textContent || 'unknown'}`,
              context: this.pageInfo?.analyticsContextKey ?? 'self',
            })
            this.fireSelectedPageChange(pages[i])
          })
        }
        pageList.appendChild(pageEntry)
      }

      // next button
      const nextButton = nextButtonTemplate.content.cloneNode(true) as HTMLElement
      const nextButtonElement = nextButton.querySelector('wa-button')
      if (nextButtonElement) {
        nextButtonElement.addEventListener('click', () => {
          if (this.pageInfo && this.pageInfo.currentPage < this.pageInfo.totalPages) {
            dispatchAnalyticEvent(Pagination.PageChangeEventName, 'click', 'ts-pagination', {
              label: 'next',
              context: this.pageInfo?.analyticsContextKey ?? 'self',
            })
            this.fireSelectedPageChange(this.pageInfo.currentPage + 1)
          }
        })
      }
      pageList?.appendChild(nextButton)
    }
  }
}

if (!customElements.get('ts-pagination')) {
  customElements.define('ts-pagination', Pagination)
}
export { Pagination }
